import React, { useCallback, useMemo } from 'react';

// DEPENDENCIES
import { createPortal } from 'react-dom';

// HOOKS
import { useSalesStatus } from '../hooks/useSalesStatus';

// GLOBAL VARIABLES
import { ENDPOINTS } from 'endpoints';

// GLOBAL COMPONENTS
import Sidebar from 'parts/sidebar/Sidebar';

// GLOBAL FUNCTIONS
import { documentFetch, fileDownload, formatDate, makePath, modalFunctions } from 'functions.js';

// HOOKS
import { useRecordContext } from 'helpers/getRecordData';

// LOCAL COMPONENTS
import BackoutForm from './BackoutForm';

// MAIN COMPONENT
const BackoutSidebar = () => {

  // HOOKS
  const {
    recordID,
    record: {
      aryTrade,
      inventory: {
        stock_num
      } = {}
    } = {},
    refetchRecord
  } = useRecordContext();

  // CALLBACKS
  const handleDownload = useCallback(
    () => {
      documentFetch({
        endpoint: makePath(ENDPOINTS.sales.print.recap, recordID),
        accept: 'pdf',
        contentType: 'pdf',
        mimeType: 'application/pdf',
        params: {
          recapType: 'Backout'
        },
        loadingMessage: 'Downloading Backout Recap',
        errorMessage: 'Unable to download Backout Recap',
        successMessage: 'Backout Recap downloaded.',
        onSuccess: ({ url }) => fileDownload(url, `backout_recap_${recordID}`)
      });
    },
    [recordID]
  );
  const handleSuccess = useCallback(
    () => {
      refetchRecord();
      modalFunctions.add({
        type: 'confirmation',
        title: 'Backout Confirmation',
        body: 'This sale has been successfully backed out.',
        continueButton: {
          label: 'Print Backout Recap',
          onClick: handleDownload
        }
      });
    },
    [handleDownload, refetchRecord]
  );
  const formatParams = useCallback(
    ({ aryTradeBackout, ...params }) => ({
      aryTradeBackout: aryTradeBackout?.map((trade, i)=> ({
        ...trade,
        purchase_trade_stock_num: trade.purchase_trade_stock_num || aryTrade[i].stock_num
      })),
      ...params
    }),
    [aryTrade]
  );

  // MEMOS
  const initialValues = useMemo(
    () => ({
      backout_date: formatDate(new Date()),
      aryTradeBackout: aryTrade?.map(({ trade_id }) => ({ trade_id }))
    }),
    [aryTrade]
  )

  // RENDER
  return createPortal(
    <Sidebar
      name="backoutSidebar"
      title="Backout"
    >
      <Sidebar.Form
        method="PUT"
        endpoint={makePath(ENDPOINTS.sales.backout, recordID)}
        initialValues={initialValues}
        formatParams={formatParams}
        onSuccess={handleSuccess}
        loadingMessage={`Backout stock # ${stock_num}`}
        successMessage={`Stock # ${stock_num} has been successfully Backout`}
        errorMessage={`Failed to backout stock # ${stock_num}`}
        body={{ vertical: 'true' }}
      >
        <BackoutForm />
      </Sidebar.Form>
    </Sidebar>,
    document.body
  );
};
const BackoutSidebarButton = props => {

  // HOOKS
  const { backoutDisabled } = useSalesStatus();

  // RENDER
  return (
    <Sidebar.Button
      name="backoutSidebar"
      label="Backout"
      icon="backout"
      disabled={backoutDisabled}
      {...props}
    />
  )  
}
BackoutSidebar.Button = BackoutSidebarButton;

// EXPORT
export default BackoutSidebar;
